import * as React from 'react';
import { Mutation } from 'react-apollo';

import { CREATE_LEAD } from '../../../../mutations';
import Wizard from '../../../../components/Form/Wizard';
import { initialValues, steps, validation } from './formConfig';
import StepOne from './Steps/StepOne';
import StepTwo from './Steps/StepTwo';
import ThankYou from '../ThankYou';
import logger from '../../../../logging';
import analytics from '../../../../analytics';

import { RequestQuoteWizardState, RequestQuoteWizardProps } from './RequestQuoteWizard.types';
import { RequestQuoteQuery } from '../../SolarContact.types';
import { RequestQuoteInitialValues } from '../../SolarContact.types';
import { FormikValues } from 'formik';
import { SolarDisqualificationType } from '../../../../analytics/Analytics';
import { parse } from '../../../../utils/calculatorUrl';

class RequestQuoteWizard extends React.Component<RequestQuoteWizardProps, RequestQuoteWizardState> {
  constructor(props: RequestQuoteWizardProps) {
    super(props);

    this.state = {
      key: 1,
      leadId: undefined,
      successfulSubmit: undefined,
      validation: validation[0],
      productType: 'SOLAR',
      asyncFetching: false,
      asyncSuccess: undefined,
      currentValues: initialValues,
      showManualAddress: false
    };

    this.onResponse = this.onResponse.bind(this);
    this.onError = this.onError.bind(this);
    this.onReset = this.onReset.bind(this);
    this.setProductType = this.setProductType.bind(this);
    this.setAsyncFetching = this.setAsyncFetching.bind(this);
    this.setDisqualification = this.setDisqualification.bind(this);
    this.onFieldChange = this.onFieldChange.bind(this);
    this.recordDisqualification = this.recordDisqualification.bind(this);
    this.enterAddressManually = this.enterAddressManually.bind(this);
  }
  //@ts-ignore
  onResponse(res): void {
    let { leadId, currentValues } = this.state;
    const { onGraphError } = this.props;

    const v = currentValues as RequestQuoteInitialValues;

    if (!leadId) {
      this.setState({
        leadId: res.createLead.lead.leadId,
        successfulSubmit: true
      });
    }

    onGraphError(false);

    const r = /[_\s]/g;
    const product = v.productType.toLowerCase().replace(r, '-');
    const propertyType = v.productType !== 'BATTERY' ? v.propertyType!.toLowerCase().replace(r, '-') : undefined;
    const roofType = v.productType !== 'BATTERY' ? v.roofType!.toLowerCase().replace(r, '-') : undefined;

    analytics.submitSuccessful({
      leadId: res.createLead.lead.leadId,
      solarProduct: product,
      propertyType: propertyType,
      roofType: roofType,
      postcode: v.postcode
    });
  }
  //@ts-ignore
  onError(res): void {
    logger.info('Handle the graphQL error response');
    logger.error('GraphQL Error', res);
    const { onGraphError } = this.props;
    onGraphError(true);
  }

  onReset(): void {
    const { key } = this.state;
    let newKey = key;

    this.setState({
      key: (newKey += 1),
      leadId: undefined,
      successfulSubmit: undefined,
      productType: initialValues.productType,
      validation: validation[0],
      asyncSuccess: undefined,
      asyncFetching: false
    });
  }

  onFieldChange(fieldName: string, value: string): void {
    const { currentValues } = this.state;

    this.setState({
      currentValues: {
        ...currentValues,
        [fieldName]: value
      }
    });
  }

  enterAddressManually = (): void => {
    this.setState({
      showManualAddress: true
    });
  };

  recordDisqualification(name: string, error: string): void {
    const { currentValues } = this.state;

    analytics.disqualify(name as SolarDisqualificationType, error, {
      postcode: currentValues.postcode,
      productType: currentValues.productType,
      roofType: currentValues.roofType,
      propertyType: currentValues.propertyType
    });
  }

  setDisqualification(value: boolean): void {
    this.setState({
      asyncSuccess: value
    });
  }

  setProductType(value: string): void {
    let flow;
    if (value === 'BATTERY') {
      flow = validation[1];
    } else {
      flow = validation[0];
    }

    this.setState({
      productType: value,
      validation: flow
    });
  }

  setAsyncFetching(asyncFetching: boolean): void {
    this.setState({
      asyncFetching
    });
  }

  getValues(values: FormikValues): RequestQuoteQuery {
    const normalizedCurrency = parseFloat(
      values.averageQuarterlyBillCost.substring(1, values.averageQuarterlyBillCost.length)
    );

    return {
      productType: values.productType,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      address: {
        unit: values.unit,
        streetNumber: values.streetNumber,
        streetName: values.streetName,
        suburb: values.suburb,
        state: values.state,
        postcode: values.postcode
      },
      propertyType: values.propertyType,
      roofType: values.roofType,
      averageQuarterlyBillCost: normalizedCurrency,
      usagePercentDuringDay: parseFloat(values.usagePercentDuringDay),
      propertyStorey: values.propertyStorey,
      powerConnectionPhaseType: values.powerConnectionPhaseType,
      roofPitch: values.roofPitch,
      allowMarketingCommunications: !values.allowMarketingCommunications,
      calculatorValues: parse(values.postcode, values.usagePercentDuringDay, normalizedCurrency) || {}
    };
  }

  render(): JSX.Element | null {
    const {
      successfulSubmit,
      productType,
      validation,
      asyncFetching,
      asyncSuccess,
      key,
      showManualAddress
    } = this.state;

    const { onGraphError, graphError, graphMessage } = this.props;

    return (
      <Mutation mutation={CREATE_LEAD} onCompleted={this.onResponse} onError={this.onError}>
        {(createLead, { loading }) => {
          return (
            <Wizard
              key={key}
              initialValues={initialValues}
              validation={validation}
              isSubmitting={loading}
              enableReinitialize={false}
              successPage={true}
              asyncFetching={asyncFetching}
              asyncSuccess={asyncSuccess}
              successfulSubmit={successfulSubmit}
              buttonLabels={steps.stepOne.form.buttons}
              onReset={this.onReset}
              onSubmit={(values: FormikValues) => {
                this.setState({
                  currentValues: values
                });
                createLead({
                  variables: {
                    input: this.getValues(values)
                  }
                });
              }}
            >
              <Wizard.Step>
                <StepOne
                  metaData={steps.stepOne}
                  productType={productType}
                  setProductType={this.setProductType}
                  asyncFetching={asyncFetching}
                  asyncSuccess={asyncSuccess}
                  setAsyncFetching={this.setAsyncFetching}
                  onFieldChange={this.onFieldChange}
                  graphError={graphError}
                  graphMessage={graphMessage}
                  onGraphError={onGraphError}
                  showManualAddress={showManualAddress}
                  enterAddressManually={this.enterAddressManually}
                  setDisqualification={this.setDisqualification}
                  recordDisqualification={this.recordDisqualification}
                />
              </Wizard.Step>
              <Wizard.Step>
                <StepTwo metaData={steps.stepTwo} />
              </Wizard.Step>
              <Wizard.Step>
                <ThankYou metaData={steps.stepOne} />
              </Wizard.Step>
            </Wizard>
          );
        }}
      </Mutation>
    );
  }
}

export { RequestQuoteWizard as default };
